import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';

export type SystemDocBarrierContext = {
  rootDocId: string;
  docIds: readonly string[];
  signal: AbortSignal;
};

export type SystemDocBarrier = (
  context: SystemDocBarrierContext
) => Promise<void>;

const barriersByHost = new WeakMap<object, Set<SystemDocBarrier>>();

export function registerSystemDocBarrier(
  host: object,
  barrier: SystemDocBarrier
): () => void {
  let barriers = barriersByHost.get(host);
  if (!barriers) {
    barriers = new Set();
    barriersByHost.set(host, barriers);
  }
  barriers.add(barrier);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    barriers.delete(barrier);
    if (!barriers.size) barriersByHost.delete(host);
  };
}

function barriersFor(host: object & { workspace?: object }) {
  return (
    barriersByHost.get(host) ??
    (host.workspace ? barriersByHost.get(host.workspace) : undefined)
  );
}

export function hasSystemDocBarrier(
  host: object & { workspace?: object }
): boolean {
  return !!barriersFor(host)?.size;
}

/** Host registrations take precedence over workspace-wide barriers. */
export async function runSystemDocBarrier(
  host: object & { workspace?: object },
  context: SystemDocBarrierContext
): Promise<void> {
  const barriers = barriersFor(host);
  if (!barriers?.size) {
    throw new BlockSuiteError(
      ErrorCode.DocCollectionError,
      'System document barrier is not registered.'
    );
  }
  await Promise.all([...barriers].map(barrier => barrier(context)));
}
